/**
 * ScamBlocker AI — Auth Store (Zustand)
 * JWT token yönetimi, kullanıcı bilgileri, RBAC.
 * Access token memory-only (XSS güvenli).
 */

import { create } from 'zustand';
import { clearTokens, setTokens } from '@/shared/lib/api/client';
import type { OrgInfo, OrgPermissions, OrgRole, StaffUser } from '@/shared/lib/api/types';

interface AuthState {
  // Data
  staff: StaffUser | null;
  org: OrgInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (staff: StaffUser, org: OrgInfo, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateStaff: (staff: Partial<StaffUser>) => void;

  // RBAC helpers
  hasPermission: (perm: keyof OrgPermissions) => boolean;
  hasRole: (...roles: OrgRole[]) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  staff: null,
  org: null,
  isAuthenticated: false,
  isLoading: true, // İlk yükleme kontrolü için

  login: (staff, org, accessToken, refreshToken) => {
    // Boş token ile çağrı canlı token'ları EZMEMELİ (2026-08-27: AuthGuard '' geçip
    // oturumu girişten hemen sonra imzasız bırakıyordu — savunma katmanı).
    if (accessToken) setTokens(accessToken, refreshToken);
    set({ staff, org, isAuthenticated: true, isLoading: false });
  },

  logout: () => {
    clearTokens();
    set({ staff: null, org: null, isAuthenticated: false, isLoading: false });
  },

  setLoading: (isLoading) => set({ isLoading }),

  updateStaff: (partial) =>
    set((state) => ({
      staff: state.staff ? { ...state.staff, ...partial } : null,
    })),

  hasPermission: (perm) => {
    const { staff } = get();
    return staff?.permissions?.[perm] ?? false;
  },

  hasRole: (...roles) => {
    const { staff } = get();
    return staff ? roles.includes(staff.role) : false;
  },
}));
